import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { PriceLine } from "@workspace/booking-rules";

/**
 * Task #4034 — shared inline price breakdown rows.
 *
 * Renders the ordered `PriceLine[]` returned by `buildPriceBreakdownLines`
 * as a simple label/value list. Callers supply `fmt` so each surface can
 * use its own currency formatter without this component depending on a hook.
 *
 * Coupon lines are displayed in green. All other lines use the standard
 * muted-foreground / foreground pair.
 */
export function PriceBreakdownLines({
  lines,
  fmt,
  className,
}: {
  lines: PriceLine[];
  fmt: (n: number) => string;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div className={cn("space-y-1.5", className)}>
      {lines.map((line) => (
        <div
          key={line.key}
          className="flex items-center justify-between text-sm"
          data-testid={`price-line-${line.key}`}
        >
          <span className="text-muted-foreground">
            {t(`pricing.line.${line.key}`)}
            {line.detail ? (
              <span className="opacity-70"> · {line.detail}</span>
            ) : null}
          </span>
          <span
            className={cn(
              "tabular-nums font-medium",
              line.key === "coupon"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-foreground",
            )}
          >
            {line.key === "delivery" && line.amount === 0
              ? t("checkout.free_label", "Free")
              : fmt(line.amount)}
          </span>
        </div>
      ))}
    </div>
  );
}
